import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { calculatorService, clientService } from '../../services/api';
import MaterialSelector from './MaterialSelector';
import CalculationResult from './CalculationResult';
import ClientSelectWithCreate from '../Common/ClientSelectWithCreate';

const CalculatorContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const CalculatorForm = styled.form`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 2rem;
`;

const FormSection = styled.div`
  margin-bottom: 2rem;
`;

const FormTitle = styled.h2`
  color: #333;
  margin-bottom: 1rem;
  border-bottom: 2px solid #e0e0e0;
  padding-bottom: 0.5rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: ${props => props.columns || '1fr'};
  gap: 1rem;
  margin-bottom: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: #555;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.3s;

  &:focus {
    outline: none;
    border-color: #4CAF50;
  }

  &:disabled {
    background-color: #f5f5f5;
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.3s;

  &:focus {
    outline: none;
    border-color: #4CAF50;
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  resize: vertical;
  min-height: 100px;

  &:focus {
    outline: none;
    border-color: #4CAF50;
  }
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: ${props => props.variant === 'secondary' ? '#6c757d' : '#4CAF50'};
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.3s;

  &:hover {
    background-color: ${props => props.variant === 'secondary' ? '#5a6268' : '#45a049'};
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

// Helper to normalize decimal input (accept both comma and dot)
const normalizeDecimal = (value) => {
  if (typeof value === 'string') {
    return value.replace(',', '.');
  }
  return value;
};

const Calculator = () => {
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState({});
  const [loading, setLoading] = useState(false);
  const [calculation, setCalculation] = useState(null);

  const { register, handleSubmit, control, watch, formState: { errors }, setValue } = useForm({
    defaultValues: {
      clientId: '',
      measurementType: 'INT',
      width: '',
      height: '',
      units: 1,
      discount: 0,
      notes: '',
      clientReference: '',
      supplierReference: '',
      molduras: [],
      passepartouts: [],
      cristales: [],
      traseras: [],
      accesorios: []
    }
  });

  // Watch measurement type and external checkbox
  const measurementType = watch('measurementType');
  const isExternal = measurementType === 'EXT';

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const [clientsRes, productsRes] = await Promise.all([
        calculatorService.getClients(),
        calculatorService.getProducts()
      ]);
      
      setClients(clientsRes.clients || []);
      setProducts(productsRes.products || {});
    } catch (error) {
      toast.error('Error al cargar datos iniciales');
      console.error(error);
    }
  };

  const handleExternalChange = (e) => {
    setValue('measurementType', e.target.checked ? 'EXT' : 'INT');
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      
      const validAcc = (data.accesorios || []).filter(a => a && (typeof a === 'string' ? a.trim() !== '' : !!(a.productId || a.id)));
      const onlyAccessories = validAcc.length > 0 &&
        (data.molduras || []).length === 0 && (data.passepartouts || []).length === 0 &&
        (data.cristales || []).length === 0 && (data.traseras || []).length === 0;

      if (!onlyAccessories && (!data.width || !data.height)) {
        toast.error('Debe ingresar las medidas');
        return;
      }

      const calculationData = {
        clientId: data.clientId,
        width: onlyAccessories && !data.width ? 0 : parseFloat(data.width),
        height: onlyAccessories && !data.height ? 0 : parseFloat(data.height),
        measurementType: data.measurementType,
        molduras: data.molduras || [],
        passepartouts: data.passepartouts || [],
        cristales: data.cristales || [],
        traseras: data.traseras || [],
        accesorios: data.accesorios || [],
        units: data.units,
        discount: data.discount,
        notes: data.notes,
        clientReference: data.clientReference,
        supplierReference: data.supplierReference
      };

      const response = await calculatorService.calculate(calculationData);
      setCalculation(response.calculation);
      
      toast.success('Cálculo realizado exitosamente');
    } catch (error) {
      toast.error('Error al realizar el cálculo');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const saveOrder = async () => {
    if (!calculation) return;

    try {
      setLoading(true);
      
      const orderData = {
        calculation,
        clientReference: watch('clientReference'),
        supplierReference: watch('supplierReference')
      };

      await calculatorService.saveOrder(orderData);
      toast.success('Pedido guardado exitosamente');
      
      // Reset form
      setCalculation(null);
    } catch (error) {
      toast.error('Error al guardar el pedido');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <CalculatorContainer>
      <CalculatorForm onSubmit={handleSubmit(onSubmit)}>
        <FormTitle>Calculadora de Marcos</FormTitle>

        <FormSection>
          <CheckboxGroup>
            <Checkbox 
              type="checkbox" 
              id="ext"
              checked={isExternal}
              onChange={handleExternalChange}
            />
            <Label htmlFor="ext">MEDIDAS EXTERIORES</Label>
          </CheckboxGroup>

          <FormRow columns="2fr 2fr 1fr">
            <FormGroup>
              <Label htmlFor="width">Ancho (cm) *</Label>
              <Input
                type="text"
                inputMode="decimal"
                {...register('width', {
                  required: 'Ancho requerido',
                  min: 1,
                  setValueAs: v => normalizeDecimal(v)
                })}
                placeholder="Ancho"
              />
              {errors.width && <span style={{ color: 'red' }}>{errors.width.message}</span>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="height">Alto (cm) *</Label>
              <Input
                type="text"
                inputMode="decimal"
                {...register('height', {
                  required: 'Alto requerido',
                  min: 1,
                  setValueAs: v => normalizeDecimal(v)
                })}
                placeholder="Alto"
              />
              {errors.height && <span style={{ color: 'red' }}>{errors.height.message}</span>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="units">Unidades</Label>
              <Input 
                type="number" 
                {...register('units', { min: 1, valueAsNumber: true })}
                min="1"
              />
            </FormGroup>
          </FormRow>

          <FormRow columns="2fr 1fr">
            <FormGroup>
              <Label htmlFor="clientId">Cliente *</Label>
              <ClientSelectWithCreate
                value={watch('clientId')}
                onChange={(clientId) => setValue('clientId', clientId)}
                onClientCreated={(newClient) => {
                  // Reload clients list
                  loadInitialData();
                }}
              />
              {errors.clientId && <span style={{ color: 'red' }}>{errors.clientId.message}</span>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="discount">Descuento (%)</Label>
              <Input
                type="text"
                inputMode="decimal"
                {...register('discount', {
                  min: 0,
                  max: 100,
                  setValueAs: v => parseFloat(normalizeDecimal(v)) || 0
                })}
              />
            </FormGroup>
          </FormRow>

          <FormRow columns="2fr 2fr">
            <FormGroup>
              <Label htmlFor="clientReference">Referencia Cliente (PC)</Label>
              <Input 
                type="text" 
                {...register('clientReference')}
                placeholder="Ej: PED-001"
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="supplierReference">Referencia Proveedor (PP)</Label>
              <Input 
                type="text" 
                {...register('supplierReference')}
                placeholder="Ej: PROV-001"
              />
            </FormGroup>
          </FormRow>
        </FormSection>

        <FormSection>
          <FormTitle>Materiales</FormTitle>
          
          <MaterialSelector
            products={products}
            register={register}
            control={control}
            setValue={setValue}
            watch={watch}
            measurementType={measurementType}
          />
        </FormSection>

        <FormSection>
          <FormRow>
            <FormGroup>
              <Label htmlFor="notes">Notas</Label>
              <TextArea 
                {...register('notes')}
                placeholder="Notas adicionales del pedido..."
              />
            </FormGroup>
          </FormRow>
        </FormSection>

        <ButtonGroup>
          <Button type="submit" disabled={loading}>
            {loading ? 'Calculando...' : 'Calcular Precio'}
          </Button>
        </ButtonGroup>
      </CalculatorForm>

      {calculation && (
        <CalculationResult 
          calculation={calculation}
          onSave={saveOrder}
          loading={loading}
        />
      )}
    </CalculatorContainer>
  );
};

export default Calculator;